import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Command as CommandPrimitive } from "cmdk";
import { Search } from "lucide-react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import {
  PALETTE_SOURCES,
  filterPaletteItems,
  type PaletteItem,
  type PaletteSource,
} from "@/components/layout/palette/paletteSources";

/**
 * cmdk always labels its input by its own (here empty) `<label>`, and overrides any
 * `aria-labelledby` prop. Re-point the input at the dialog's one sr-only title instead,
 * so the name "Search or jump to" exists once. React never rewrites cmdk's constant value.
 */
function labelInputByDialogTitle(input: HTMLInputElement | null) {
  const titleId = input?.closest('[role="dialog"]')?.getAttribute("aria-labelledby");
  if (input && titleId) input.setAttribute("aria-labelledby", titleId);
}

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface PaletteGroupProps {
  source: PaletteSource;
  query: string;
  open: boolean;
  onSelect: (item: PaletteItem) => void;
  onLoadingChange: (sourceId: string, isLoading: boolean) => void;
}

/** One source = one group. Its `useItems` hook is called unconditionally while mounted. */
function PaletteGroup({ source, query, open, onSelect, onLoadingChange }: PaletteGroupProps) {
  const { items, isLoading } = source.useItems(query, open);
  const ready = query.trim().length >= source.minQueryLength;
  const shown = ready ? filterPaletteItems(items, query) : [];
  const loading = ready && isLoading;

  useEffect(() => {
    onLoadingChange(source.id, loading);
  }, [source.id, loading, onLoadingChange]);

  if (shown.length === 0) return null;
  return (
    <CommandPrimitive.Group
      heading={source.heading}
      className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-ds-ink3"
    >
      {shown.map((item) => {
        const Icon = item.icon;
        return (
          <CommandPrimitive.Item
            key={item.id}
            value={`${source.id}:${item.id}`}
            onSelect={() => onSelect(item)}
            className="flex cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] text-ds-ink outline-none data-[selected=true]:bg-ds-sunk"
          >
            <Icon className="h-[15px] w-[15px] flex-none text-ds-ink2" aria-hidden="true" />
            <span className="truncate">{item.label}</span>
            {item.hint && <span className="ml-auto flex-none text-xs text-ds-ink3">{item.hint}</span>}
          </CommandPrimitive.Item>
        );
      })}
    </CommandPrimitive.Group>
  );
}

/**
 * Ctrl/⌘ K "Search or jump to…" palette (C-CMS-DS, plans/ps-cms-shell.md T5), mounted
 * once in `Shell`. Groups come from the pluggable `PALETTE_SOURCES` registry; Enter /
 * click navigates to the item and closes; Esc / overlay closes.
 */
export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [, navigate] = useLocation();
  const [query, setQuery] = useState("");
  const [loadingSources, setLoadingSources] = useState<Record<string, boolean>>({});
  // The element focused before the palette opened (its button, or wherever Ctrl/⌘ K was pressed).
  // There is no Radix Trigger, so Radix would otherwise drop focus on <body> when it closes.
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Global Ctrl+K / ⌘K — ignored when the palette is already open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // `key` is missing on some synthetic keydowns (autofill); `code` keeps non-Latin layouts working.
      const isK = event.key?.toLowerCase() === "k" || event.code === "KeyK";
      if (!isK || !(event.ctrlKey || event.metaKey)) return;
      event.preventDefault();
      if (!open) onOpenChange(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  // A fresh query every time the palette opens.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const handleLoadingChange = useCallback((sourceId: string, isLoading: boolean) => {
    setLoadingSources((prev) => (Boolean(prev[sourceId]) === isLoading ? prev : { ...prev, [sourceId]: isLoading }));
  }, []);

  const handleSelect = (item: PaletteItem) => {
    navigate(item.href);
    onOpenChange(false);
  };

  const anyLoading = Object.values(loadingSources).some(Boolean);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Mockup .palette overlay rgba(10,17,23,.45) = the dark nav colour; panel edge = ds line (both themes). */}
        <DialogPrimitive.Overlay
          data-palette-overlay=""
          className="fixed inset-0 z-50 bg-ds-nav/45 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none"
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onOpenAutoFocus={() => {
            const active = document.activeElement;
            restoreFocusRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
          }}
          onCloseAutoFocus={(event) => {
            const target = restoreFocusRef.current;
            restoreFocusRef.current = null;
            if (target && target.isConnected) {
              event.preventDefault();
              target.focus();
            }
          }}
          className={cn(
            "fixed left-1/2 top-[90px] z-50 w-[calc(100%-2rem)] max-w-[560px] -translate-x-1/2 overflow-hidden rounded-ds-lg border border-ds-line bg-ds-panel font-ds text-ds-ink shadow-[0_20px_60px_rgb(0_0_0/0.3)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 motion-reduce:animate-none",
          )}
        >
          <DialogPrimitive.Title className="sr-only">Search or jump to</DialogPrimitive.Title>
          {/* No cmdk `label`: the sr-only title above is the only copy of the name; the input points at it. */}
          <CommandPrimitive shouldFilter={false} loop className="flex flex-col">
            <div className="flex h-12 items-center gap-2.5 border-b border-ds-line px-4">
              <Search className="h-[18px] w-[18px] flex-none text-ds-ink3" aria-hidden="true" />
              <CommandPrimitive.Input
                ref={labelInputByDialogTitle}
                value={query}
                onValueChange={setQuery}
                placeholder="Search or jump to…"
                className="h-full w-full bg-transparent text-[15px] text-ds-ink outline-none placeholder:text-ds-ink3"
              />
            </div>
            <CommandPrimitive.List className="max-h-[360px] overflow-y-auto p-1.5">
              {anyLoading ? (
                <CommandPrimitive.Loading>
                  <p className="px-2.5 py-2 text-[13px] text-ds-ink3">Searching…</p>
                </CommandPrimitive.Loading>
              ) : (
                <CommandPrimitive.Empty className="px-2.5 py-6 text-center text-[13.5px] text-ds-ink2">
                  No results for "{query}".
                </CommandPrimitive.Empty>
              )}
              {PALETTE_SOURCES.map((source) => (
                <PaletteGroup
                  key={source.id}
                  source={source}
                  query={query}
                  open={open}
                  onSelect={handleSelect}
                  onLoadingChange={handleLoadingChange}
                />
              ))}
            </CommandPrimitive.List>
          </CommandPrimitive>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
